import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem, Chip, Autocomplete, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { employeesAPI, filesAPI } from '../../services/api';
import { EMPLOYMENT_TYPES, NAVIGATION, ROLES } from '../../constants';
import { useTechnologyValues, useTechnologyLabels } from '../../hooks/useTechnologies';
import { POSITIONS } from '../../constants';
import ProfilePictureUpload from '../../components/ProfilePictureUpload';

const schema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(6),
  birthDate: z.string().min(1),
  hireDate: z.string().min(1),
  salary: z.string().optional(),
  bankAccount: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  emergencyContact: z.string().optional(),
  role: z.enum([ROLES.ADMIN, ROLES.EMPLOYEE]),
  employmentType: z.enum([EMPLOYMENT_TYPES.CONTRACT, EMPLOYMENT_TYPES.FREELANCE]).optional(),
  position: z.string().optional(),
  technologies: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof schema>;

const AddEmployee: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [selectedProfilePictureFile, setSelectedProfilePictureFile] = useState<File | null>(null);
  
  const handleProfilePictureChange = (imageUrl: string | null, file?: File) => {
    setProfilePictureUrl(imageUrl);
    setSelectedProfilePictureFile(file || null);
  };
  
  // Fetch technology data
  const { data: technologyValues = [], isLoading: technologyValuesLoading } = useTechnologyValues();
  const { data: technologyLabels = {}, isLoading: technologyLabelsLoading } = useTechnologyLabels();
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      hireDate: '',
      salary: '',
      bankAccount: '',
      address: '',
      phone: '',
      emergencyContact: '',
      role: ROLES.EMPLOYEE,
      employmentType: EMPLOYMENT_TYPES.CONTRACT,
      position: POSITIONS[0] as string,
      technologies: [],
    }
  });
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');

  const onSubmit = async (data: FormData) => {
    setSuccess(''); setError('');
    try {
      const payload: any = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        birthDate: data.birthDate ? new Date(data.birthDate).toISOString() : undefined,
        hireDate: data.hireDate ? new Date(data.hireDate).toISOString() : undefined,
        role: data.role
      };
      if (data.salary) payload.salary = parseFloat(data.salary);
      if (data.bankAccount) payload.bankAccount = data.bankAccount;
      if (data.address) payload.address = data.address;
      if (data.phone) payload.phone = data.phone;
      if (data.emergencyContact) payload.emergencyContact = data.emergencyContact;
      if (data.employmentType) payload.employmentType = data.employmentType;
      if (data.position) payload.position = data.position;
      if (data.technologies && data.technologies.length > 0) payload.technologies = data.technologies;
      const response = await employeesAPI.create(payload);
      const employeeId = response.id;
      
      // Upload profile picture if one was selected
      if (selectedProfilePictureFile && employeeId) {
        try {
          await filesAPI.uploadProfilePicture(selectedProfilePictureFile, 'EMPLOYEE', employeeId);
        } catch (profileError) {
          console.error('Profile picture upload failed:', profileError);
          // Don't fail the entire operation if profile picture upload fails
        }
      }
      
      setSuccess('Employee added successfully!');
      reset();
      setProfilePictureUrl(null);
      setSelectedProfilePictureFile(null);
      navigate(NAVIGATION.EMPLOYEES);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add employee');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Add New Employee</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Profile Picture Upload */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ProfilePictureUpload
              currentImageUrl={profilePictureUrl}
              entityType="EMPLOYEE"
              entityId="new" // Will be updated after employee creation
              onImageChange={(imageUrl, file) => handleProfilePictureChange(imageUrl, file)}
              disabled={isSubmitting}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="First Name" fullWidth margin="normal" {...register('firstName')} error={!!errors.firstName} helperText={errors.firstName?.message} />
            <TextField label="Last Name" fullWidth margin="normal" {...register('lastName')} error={!!errors.lastName} helperText={errors.lastName?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField 
              label="Birth Date" 
              type="date" 
              fullWidth 
              margin="normal" 
              {...register('birthDate')} 
              error={!!errors.birthDate} 
              helperText={errors.birthDate?.message}
              InputLabelProps={{ shrink: true }}
            />
            <TextField label="Email" fullWidth margin="normal" {...register('email')} error={!!errors.email} helperText={errors.email?.message} />
            <TextField 
              label="Password" 
              type={showPassword ? 'text' : 'password'} 
              fullWidth 
              margin="normal" 
              {...register('password')} 
              error={!!errors.password} 
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Hire Date" type="date" fullWidth margin="normal" {...register('hireDate')} InputLabelProps={{ shrink: true }} error={!!errors.hireDate} helperText={errors.hireDate?.message} />
            <TextField label="Salary" fullWidth margin="normal" {...register('salary')} error={!!errors.salary} helperText={errors.salary?.message} />
            <TextField label="Bank Account" fullWidth margin="normal" {...register('bankAccount')} error={!!errors.bankAccount} helperText={errors.bankAccount?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Address" fullWidth margin="normal" {...register('address')} error={!!errors.address} helperText={errors.address?.message} />
            <TextField label="Phone" fullWidth margin="normal" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} />
            <TextField label="Emergency Contact" fullWidth margin="normal" {...register('emergencyContact')} error={!!errors.emergencyContact} helperText={errors.emergencyContact?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField
              label="Role"
              select
              fullWidth
              margin="normal"
              {...register('role')}
              error={!!errors.role}
              helperText={errors.role?.message}
              value={watch('role') || ''}
            >
              <MenuItem value={ROLES.EMPLOYEE}>Employee</MenuItem>
              <MenuItem value={ROLES.ADMIN}>Admin</MenuItem>
            </TextField>
            <TextField
              label="Employment Type"
              select
              fullWidth
              margin="normal"
              {...register('employmentType')}
              error={!!errors.employmentType}
              helperText={errors.employmentType?.message}
              value={watch('employmentType') || ''}
            >
              <MenuItem value={EMPLOYMENT_TYPES.CONTRACT}>Contract</MenuItem>
              <MenuItem value={EMPLOYMENT_TYPES.FREELANCE}>Freelance</MenuItem>
            </TextField>
            <TextField
              label="Position"
              select
              fullWidth
              margin="normal"
              {...register('position')}
              error={!!errors.position}
              helperText={errors.position?.message}
              value={watch('position') || ''}
            >
              {POSITIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Autocomplete
            multiple
            options={technologyValues}
            value={watch('technologies') || []}
            onChange={(_, newValue) => setValue('technologies', newValue)}
            getOptionLabel={(option) => technologyLabels[option] || option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Technologies"
                margin="normal"
                error={!!errors.technologies}
                helperText={errors.technologies?.message}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={technologyLabels[option] || option}
                    size="small"
                    {...chipProps}
                  />
                );
              })
            }
            filterSelectedOptions
            loading={technologyValuesLoading || technologyLabelsLoading}
          />
        </Box>
        <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Employee'}
        </Button>
      </form>
    </Box>
  );
};

export default AddEmployee; 